import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Header } from '@/components/Header';
import { BottomNav } from '@/components/BottomNav';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Trophy, Users, TrendingUp, Plus, ArrowRight, Bell, ArrowRightLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Dashboard = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [leagues, setLeagues] = useState([]);
  const [pendingTrades, setPendingTrades] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingTrades, setIsLoadingTrades] = useState(true);

  const fetchLeagues = useCallback(async () => {
    // Don't fetch if no token
    if (!token) {
      console.log('⚠️ [Dashboard] No token available, skipping fetch');
      setIsLoading(false);
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/leagues`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setLeagues(data);
      } else if (response.status === 401) {
        console.log('⚠️ [Dashboard] 401 Unauthorized');
      }
    } catch (err) {
      toast.error('Errore nel caricamento delle leghe');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  const fetchPendingTrades = useCallback(async () => {
    if (!token || !leagues.length) {
      setIsLoadingTrades(false);
      return;
    }

    try {
      // Fetch trades for all leagues
      const tradesPromises = leagues.map(league =>
        fetch(`${API_URL}/api/trades/${league.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then(res => res.ok ? res.json() : [])
      );

      const allTrades = await Promise.all(tradesPromises);
      
      // Flatten and filter for pending trades where user is the recipient
      const pending = allTrades.flat().filter(trade => 
        trade.status === 'pending' && 
        trade.to_team_owner_id === user?.id
      );

      // Add league info to each trade
      const tradesWithLeague = pending.map(trade => ({
        ...trade,
        league: leagues.find(l => l.id === trade.league_id)
      }));

      setPendingTrades(tradesWithLeague);
    } catch (err) {
      console.error('Error fetching trades:', err);
    } finally {
      setIsLoadingTrades(false);
    }
  }, [leagues, token, user?.id]);

  useEffect(() => {
    fetchLeagues();
  }, [fetchLeagues]);

  useEffect(() => {
    if (leagues.length > 0) {
      fetchPendingTrades();
    }
  }, [fetchPendingTrades, leagues.length]);

  return (
    <div className="min-h-screen bg-background">
      <Header title="FANTACLASSE" showMenu />
      
      <main className="main-content p-4 max-w-5xl mx-auto">
        {/* Welcome Section */}
        <section className="mb-8 animate-fadeIn">
          <h2 
            className="text-2xl md:text-3xl font-bold mb-2"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="dashboard-welcome"
          >
            Ciao, {user?.name?.split(' ')[0]}!
          </h2>
          <p className="text-muted-foreground">
            {leagues.length > 0 
              ? `Stai partecipando a ${leagues.length} ${leagues.length === 1 ? 'lega' : 'leghe'}`
              : 'Non sei ancora in nessuna lega. Creane una o unisciti!'
            }
          </p>
        </section>

        {/* Pending Trades Notifications */}
        {!isLoadingTrades && pendingTrades.length > 0 && (
          <section className="mb-8">
            <Card className="border-primary/50 bg-primary/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Bell className="h-5 w-5 text-primary" />
                  Scambi Pendenti
                  <Badge variant="default" className="ml-auto">
                    {pendingTrades.length}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {pendingTrades.map((trade) => (
                  <Card 
                    key={trade.id}
                    className="cursor-pointer hover:bg-accent transition-colors"
                    onClick={() => navigate(`/leagues/${trade.league_id}`)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <ArrowRightLeft className="h-4 w-4 text-primary" />
                            <span className="font-semibold text-sm">
                              Proposta di Scambio
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground mb-1">
                            In <span className="font-medium text-foreground">{trade.league?.name}</span>
                          </p>
                          <p className="text-sm">
                            <span className="font-medium">{trade.from_professor_name}</span>
                            {' '}↔{' '}
                            <span className="font-medium">{trade.to_professor_name}</span>
                          </p>
                        </div>
                        <ArrowRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate('/leagues')}
                >
                  Visualizza tutti gli scambi
                </Button>
              </CardContent>
            </Card>
          </section>
        )}

        {/* Quick Stats */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="hover-lift" data-testid="stat-leagues">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {leagues.length}
                </p>
                <p className="text-xs text-muted-foreground">Leghe</p>
              </div>
            </CardContent>
          </Card>

          <Card className="hover-lift" data-testid="stat-trades">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                <ArrowRightLeft className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {isLoadingTrades ? <Loader2 className="h-5 w-5 animate-spin" /> : pendingTrades.length}
                </p>
                <p className="text-xs text-muted-foreground">Scambi</p>
              </div>
            </CardContent>
          </Card>

          <Card className="hover-lift col-span-2 md:col-span-2" data-testid="stat-position">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                <Trophy className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">Miglior posizione</p>
                <p className="font-semibold">
                  {leagues.length > 0 ? 'Vai alla classifica per vedere' : 'Nessuna lega attiva'}
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Actions */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <Button
            variant="default"
            className="h-auto py-4 rounded-xl justify-start gap-4 btn-press"
            onClick={() => navigate('/leagues')}
            data-testid="dashboard-create-league-btn"
          >
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <Plus className="w-5 h-5" />
            </div>
            <div className="text-left">
              <p className="font-semibold">Crea una Lega</p>
              <p className="text-xs opacity-80">Invita i tuoi compagni di classe</p>
            </div>
          </Button>

          <Button
            variant="outline"
            className="h-auto py-4 rounded-xl justify-start gap-4 btn-press"
            onClick={() => navigate('/leagues')}
            data-testid="dashboard-join-league-btn"
          >
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-primary" />
            </div>
            <div className="text-left">
              <p className="font-semibold">Unisciti a una Lega</p>
              <p className="text-xs text-muted-foreground">Inserisci il codice invito</p>
            </div>
          </Button>
        </section>

        {/* My Leagues */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h3 
              className="text-lg font-semibold"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Le mie Leghe
            </h3>
            {leagues.length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => navigate('/leagues')}>
                Vedi tutte
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : leagues.length > 0 ? (
            <div className="space-y-3 stagger-children">
              {leagues.slice(0, 3).map((league) => (
                <Card
                  key={league.id}
                  className="hover-lift cursor-pointer"
                  onClick={() => navigate(`/leagues/${league.id}`)}
                  data-testid={`league-card-${league.id}`}
                >
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Trophy className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold">{league.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {league.members.length} {league.members.length === 1 ? 'membro' : 'membri'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {(league.owners?.includes(user?.id) || league.owner_id === user?.id) && (
                        <Badge variant="secondary">Owner</Badge>
                      )}
                      {league.admins?.includes(user?.id) && !league.owners?.includes(user?.id) && league.owner_id !== user?.id && (
                        <Badge variant="outline">Admin</Badge>
                      )}
                      <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="border-dashed" data-testid="no-leagues-card">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-muted mx-auto mb-4 flex items-center justify-center">
                  <Users className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="font-semibold mb-2">Nessuna lega ancora</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Crea una lega o unisciti a una esistente per iniziare a giocare
                </p>
                <Button onClick={() => navigate('/leagues')}>
                  Vai alle Leghe
                </Button>
              </CardContent>
            </Card>
          )}
        </section>
      </main>

      <BottomNav />
    </div>
  );
};
